import {Location} from 'Common/Location';
import {InvalidFieldValueError} from 'Browser/InvalidFieldValueError';
import {palette} from 'Shared/artists/Palette';
import {Setter,onMount,Show,For,createSignal,createMemo, onCleanup} from 'solid-js';
import { HtmlEditorWidget } from 'Browser/HtmlEditorWidget';
import { css } from '@emotion/css';
import { useUrls } from 'Shared/SiteProvider';
import { IStoreWrap, useStore } from 'Shared/StoreProvider';
import { dbPatchField } from 'Shared/forms/BaseOperations';
import { splitLocation, subdoc } from 'Common/ViewUtils';
import { Select } from '@thisbeyond/solid-select';
import '@thisbeyond/solid-select/style.css';
import Assert from 'Common/Assert';


export const fieldStyle = (narrow:boolean,disable:boolean) => css({
	font: '15px Arial, Helvetica, sans-serif',
	backgroundColor: 'inherit',
	borderTop: 'none',
	borderRight: 'none',
	borderLeft: 'none',
	borderImage: 'initial',
	borderBottom: '1px solid rgb(239, 239, 239)',
	padding: '5px 10px 5px 0',
	width: narrow ? 'unset' : '100%',
	color: disable ? 'rgb(120, 120, 120)' : 'rgb(85, 85, 85)',

	':focus': {
		borderColor: 'rgb(102, 175, 233)',
		outline: 0,
		boxShadow: 'rgba(0, 0, 0, 0.075) 0px 1px 1px inset, rgba(102, 175, 233, 0.6) 0px 0px 8px'
	},
});

export const inputStyle = (narrow:boolean,disable:boolean) => css(fieldStyle(narrow,disable), {
	transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',

	'::placeholder': {
		fontSize: 14,
		fontStyle: 'italic'
	}
});

export const textStyle = (narrow:boolean,disable:boolean) => css(inputStyle(narrow,disable), {
	/* This needs to a bit darker than the default as we need to see it properly for Text components */
	borderBottom: '1px solid rgb(222, 222, 222)',
});

//XXX not currently used
export const textAreaStyle = () => css(fieldStyle(false,false), {
	transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
});

export const selectStyle = (narrow:boolean=true) => css(fieldStyle(narrow,false));

const checkboxStyle = () => css({
	width: 'initial'  //TODO remove after Less gone?
});

const priceStyle = () => css({
	display: 'flex',
	gap: '0.1em',
	alignItems: 'center'
});

export const integerStyle = (disable:boolean) => css(inputStyle(false,disable), {
	width: '6em',
	textAlign: 'right'
});

export const multiSelectStyle = () => css(fieldStyle(false,false),{
	border: 'none',

	'.solid-select-list': {
		fontSize: 12,
		backgroundColor: 'white'
	},
});


export interface IField {
	field:string;
	ref?: HTMLDivElement;
	class?: string;
	disable?:boolean;
	processInput?: (value:string) => any;
	/* TODO implement. This should replace the default update procedure altogether. */
	update?: () => Promise<void>;
}


export interface IText extends IField {
	maxLength?: number;
	narrow?: boolean;
	placeholder?: string;
	onInput?: (value:string) => void;
}

export function Text(P:IText)  
{
	const sw = useStore();

	//TODO if there is an error probably should re-evaluate it every key press...
	//     separate the onChange code? NB annoying when fields complain too soon though.

	const [error,setError] = createSignal(undefined as undefined|string);

	return (<>
		<input type='text'
			id={id(P.field)}
			class={css(textStyle(P.narrow,P.disable),P.class ?? {})} name='' autocomplete='off'
			maxlength={P.maxLength ?? 255}
			value={getValue(sw.location,P.field) ?? ''}
			placeholder={P.placeholder}
			ref={P.ref}
			onChange={e => updateField(e.target,sw,P.field,P.processInput,setError)}
			onInput={e => P.onInput?.(e.target.value)  } 
			style={error() ? 'background-color:'+palette.errorBackground : ''}
			data-error={error() ?? ''}
			disabled={P.disable}
		/>
	</>);
}

interface IMultiLineText extends IField {
	numRows:number;
	maxLength?:number;
}

/* XXX if I keep using this, probably want to support paragraphs */
export function MultiLineText(P:IMultiLineText)
{
	const sw = useStore();

	const [error,setError] = createSignal(undefined as undefined|string);

	return (
		<textarea id={id(P.field)}
			class={inputStyle(false,false)} name='' autocomplete='off'
			rows={P.numRows}
			maxlength={P.maxLength ?? 2000}
			onChange={e => updateField(e.target,sw,P.field,P.processInput,setError)}
			style={error() ? 'background-color:'+palette.errorBackground : ''}
			data-error={error() ?? ''}
		>
			{getValue(sw.location,P.field)}
		</textarea>
	);
}

interface ISingleSelect extends IField {
	options:Record<string,string>;
	required?:boolean;
	placeholder?:string;
	wide?:boolean ;
	/* 
		Show the label for this value when the value is null. 
		WARNING It is assumed that the client code will interpret null as being this value, this value is NOT stored in the DB.
	*/
	defaultValue?:any;
}

export function SingleSelect(P:ISingleSelect)
{
	const sw = useStore();

	const required = () => P.required || !!P.defaultValue;

//	const value = () => getValue(sw.location,P.field);

	const value = createMemo(() => {
		//XXX odd hack Im using to get initParent to work...
		if (!sw) return undefined;

		return getValue(sw.location,P.field);
	});


	const [error,setError] = createSignal(undefined as undefined|string);

	return (
		<select id={id(P.field)}
			class={css(selectStyle(!P.wide),P.class ?? {})}
			name='' 
			autocomplete='off' 
			disabled={P.disable}
			onChange={e => updateField(e.target,sw,P.field,P.processInput,setError)}
			style={error() ? 'background-color:'+palette.errorBackground : ''}
			data-error={error() ?? ''}
		>
			{/*
				Showing '(none)' when 'required' is safer when there is no value than defaulting to 
				the first option as the DB won't be updated with the first value unless the user 
				changes the selected value. 
					In time maybe use JS to remove (none) on first select.
					There is a default option in DbTables which can/should always/sometimes be used			*/} 
			<Show when={!required() || (!value() && !P.defaultValue)}>
				<option value='' selected={value()==undefined} >
					{P.placeholder ?? '(none)'}
				</option>
			</Show>

			<For each={Object.entries(P.options)}>{ item => 
				<option value={item[0]} selected={item[0]==value() || (!value() && item[0]==P.defaultValue)}>
					{item[1]}
				</option>
			}</For>
		</select>
	);
}

export function Checkbox(P:IField)
{
	const sw = useStore();

	const [error,setError] = createSignal(undefined as undefined|string);

	return (
		<input type='checkbox' class={checkboxStyle()} name='' 
			id={id(P.field)}
			checked={getValue(sw.location,P.field)}
			onChange={e => updateFieldWithValue(sw,P.field,e.target.checked ?? false,setError)}
		/>
	);
}

interface IInteger extends IField {
	min?:number;
	max?:number;
	step?:number|'any';
	placeholder?:string;
	start?:string
}

export function Integer(P:IInteger)
{
	const sw = useStore();

	const processInput = (numStr:string|undefined) => {
		if (numStr==undefined) return undefined;
		const num =  Number.parseInt(numStr);
		return P.processInput ? P.processInput(num) : num;
	};

	const value = () => getValue(sw.location,P.field);

	return (
		<input type='number'
			id={id(P.field)}
			class={css(integerStyle(value()==undefined),P.class ?? {})}
			name='' 
			value={value() ?? P.start}
			max={P.max} min={P.min} step={P.step ?? '1'}
			onChange={e => updateField(e.target,sw,P.field,processInput)}
			disabled={P.disable}
			placeholder={P.placeholder ?? ''}
		/>
	);
}

export function NumberField(P:IInteger)
{
	const sw = useStore();

	const processInput = (numStr:string|undefined) => {
		const num = isNaN(Number(numStr)) ? 0 : Number(numStr);
		return P.processInput ? P.processInput(num) : num;
	};

	return (
		<input type='number'
			id={id(P.field)}
			class={css(integerStyle(false),P.class ?? {})}
			name='' value={getValue(sw.location,P.field)}
			max={P.max} min={P.min} step={P.step ?? '1'}
			onChange={e => updateField(e.target,sw,P.field,processInput)}
			disabled={P.disable}
			placeholder={P.placeholder ?? ''}
		/>
	);
}

interface IMultiSelect extends IField {
	options:Record<string,string>;
}

export function MultiSelect(P:IMultiSelect)
{
	//WARNING there can be z-index problems with the library I'm using here. Hopefully the library will fix it sometime

	const sw = useStore();

	const value = createMemo(() => {
		/* Remove any selected items that aren't currently in the list of options: */
		const options = [];
		for (const key of getValue(sw.location,P.field) ?? []) 
			if (P.options[key])
				options.push(key);
		return options;
	});


	/* Annoyingly Solid Select triggers onChange when initialising, so prevent this from happening */
	let initialising = true;

	const change = value => {
		if (initialising) {
			initialising = false;
			return;
		}
		updateFieldWithValue(sw,P.field,value,null,P.processInput);
	};

	const unselected = () => createMemo(() => {
		const options = {...P.options};
		for (const key of getValue(sw.location,P.field) ?? []) 
			if (P.options[key])
				delete options[key];
		return Object.keys(options);
	});

	//FIXME use the standard focus styling
	return (
		<Select class={P.class ?? multiSelectStyle()} multiple options={unselected()} format={key => P.options[key]} 
			onChange={change} initialValue={value()} placeholder='' disabled={P.disable}
		/>
	);
}

interface IPrice extends IField {
	maxLength?:number;
	placeholder?:string;
}

export function Price(P:IPrice)
{
	const sw = useStore();

	const [error,setError] = createSignal(undefined as undefined|string);

	return (
		<div class={priceStyle()}>
			$ <input type='text' class={inputStyle(false,false)}
				id={id(P.field)}
				name='' autocomplete='off'
				maxlength={P.maxLength ?? 255}
				value={getValue(sw.location,P.field) ?? ''}
				placeholder={P.placeholder ?? 'X.XX'}
				onChange={e => updateField(e.target,sw,P.field,P.processInput,setError)}
			/>
		</div>
	);
}


interface IHtml extends IField { 
	placeholder?: string;
	options;
}

//TODO merge this function with HtmlEditorWidget
export function Html(P:IHtml)
{
	const urls = useUrls();
	const sw = useStore();

	let htmlEditor!:HtmlEditorWidget;
	let htmlNode!: HTMLDivElement;

	onMount(() => {
		htmlEditor = new HtmlEditorWidget(urls,P.options);
		htmlEditor.init(htmlNode)
	});
	onCleanup(() => htmlEditor?.destroy(htmlNode));

	return (
		<div ref={htmlNode} id={id(P.field)} onChange={e => updateField(e.target,sw,P.field,P.processInput)}>
			<div id={`hugerte-${id(P.field)}`} innerHTML={getValue(sw.location,P.field)} placeholder={P.placeholder}></div>
		</div>
	);
}


/* --- Private functions: --- */

export function id(field:string)
{
	const sw = useStore();
	return sw.permission+'-'+JSON.stringify(sw.location ?? [])+'-'+field;
}

export function getValue(location:Location,field:string)
{
	const sw = useStore();

	let pos = sw.store;
	for (const part of location ?? []) 
		pos = pos[part];

	return pos==undefined ? undefined : pos[field];
}

export async function updateField<D>(node:HTMLElement,sw:IStoreWrap<D>,field:string,processInput:((value:string)=>unknown)|undefined,setError?:Setter<string|undefined>)
{
//TODO add support for a custom 'update' callback to be used instead (eg see ColourPicker.tsx code).


	/* Swap whitespace strings with undefined */
	Assert.property('value',node);
	const val1 = (node as any).value.trim();
	const val = val1=='' ? undefined : val1;

	const value = val!=undefined && processInput ? processInput(val) : val;
	await updateFieldWithValue<D>(sw,field,value,setError);
}

export async function updateFieldWithValue<D>(sw:IStoreWrap<D>,field:string,value:any,setError?:Setter<string|undefined>)
{
	if (setError)
		setError(undefined);

	try {
		let ret;

		/* 
			The awaits here are important - if afterUpdate() contains a DB update then tRPC will batch the operations and this
			can lead to two updates being performed simultaneously on the same doc leading to one DB update effectly disappearing.
		*/

		if (sw.prepareParent)
			ret = await updateParent(sw,field,value);
		else 
			ret = await dbPatchField(sw,field,value);

		if (sw.afterUpdate)
			await sw.afterUpdate(value,ret);
	}
	catch(err) {
		if (err instanceof InvalidFieldValueError && setError)
			setError(err.message);
		else
			throw err;
	}
}

/* Create/destroy the parent node as well as the current field: */
async function updateParent<D>(sw:IStoreWrap<D>,field:string,value:any)
{
	const previous = subdoc(sw.store,sw.location);
	const parentObject = previous ?? await sw.prepareParent?.(field,value);

	//XXX Its somewhat possible (but unlikely) that we may need to support the creation of arrays as well as objects
	const [path,parentLocation] = splitLocation(sw.location);

	/* WARNING using ...sw instead here seems to trigger an update */
	const sw2 = {setStore:sw.setStore,server:sw.server,page:sw.page,permission:sw.permission,docId:sw.docId,location:path};

	let fields = {...parentObject,[field]:value};

	//XXX cf a 'cleanup' option, or automatic removal of parent objects when not used

	return await dbPatchField(sw2,parentLocation,fields);
}

